import { AppBar, Autocomplete, Avatar, FormControl, ListItemText, TextField, Toolbar, Tooltip } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import { SyntheticEvent, useState } from 'react'
import { ColoredIconButton } from '../common/ColoredIconButton'
import { TransparentIconButton } from '../common/TransparentIconButton'
import { Add, EditRounded } from '@mui/icons-material'
import { useSelector } from '../../redux/hooks/useSelector'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { newThemeObject } from '../../newTheme'
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import { Client } from '../../redux/reducers/clients'
import { tokens } from '../../styles/tokens'
import { openDialog } from '../../redux/actions/dialogActions'
import { CLIENT_INFORMATION_DIALOG_ID, DEPOSIT_DIALOG_ID } from '../../constants/dialogConstants'
import { isDistributorUser } from '../../utils/distributor'
import { isApexUser } from '../../utils/apex'
import { canExportClientDetail } from '../../utils/roles'
import { isClientUser } from '../../services/keycloak.service'
import { AlertMessageConfirmation } from '../dialogs/AlertMessageConfirmation'
import { useClientApi } from '../../hooks/useClientApi'

const useStyles = makeStyles(theme => ({
  tableTopBar: {
    backgroundColor: tokens.color.white,
    border: `1px solid ${tokens.color.neutral[200]}`,
    boxShadow: 'none',
    borderRadius: newThemeObject.roundness.basic,
    margin: '16px 0',
  },
  tableToolBar: {
    minHeight: 0,
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(2),
    margin: 0,
    flexDirection: 'column',
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  separator: {
    flexGrow: 1,
  },
  textField: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 400,
    },
    marginRight: theme.spacing(1),
  },
  icon: {
    color: newThemeObject.palette.main.sectext,
    backgroundColor: tokens.color.white,
  },
  clientInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  avatar: {
    height: '2rem',
    width: '2rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: tokens.font.weight.semibold,
    letterSpacing: '-0.01em',
    color: tokens.color.neutral[900],
  },
  subtitle: {
    fontSize: '0.75rem',
    color: tokens.color.neutral[500],
  },
  text: {
    margin: 0,
  },
}))

interface ClientTableTopBarProps {
  setSelectedClient: (client: Client | null) => any
}

export const ClientDetailTopBar = ({
  setSelectedClient,
}: ClientTableTopBarProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const clients = useSelector(state => state.clients.clients)
  const client = useSelector(state => state.clients.client)
  const user = useSelector(state => state.auth.user)
  const { exportClientDetail } = useClientApi()
  const [showExportConfirmation, setShowExportConfirmation] = useState<boolean>(false)

  let name = ''
  if (client.company) {
    name = client.companyName
  } else {
    if (!!client.firstName) {
      name += client.firstName
    }
    if (!!client.surname) {
      if (name.length > 0) name += ' ' + client.surname
      else name += client.surname
    }
  }
  const symbols = !!client.firstName
    ? client.firstName.charAt(0).toUpperCase() + client.surname.charAt(0).toUpperCase()
    : undefined

  return (
    <AppBar position="static" className={classes.tableTopBar} elevation={0}>
      <Toolbar variant="dense" className={classes.tableToolBar}>

        <span className={classes.clientInfo}>
          <Avatar className={classes.avatar}>{symbols}</Avatar>
          <span>
            <p className={classNames(classes.text, classes.title)}>{name}</p>
            <p className={classNames(classes.text, classes.subtitle)}>{client.personalNumber}</p>
          </span>
          {!isDistributorUser(user) && (
            <TransparentIconButton
              onClick={() => {
                dispatch(
                  openDialog(CLIENT_INFORMATION_DIALOG_ID, {
                    dialogTitle: t('CLIENT_EDIT'),
                    client,
                    type: 'edit',
                  })
                )
              }}
              icon={<EditRounded />}
            />
          )}
        </span>

        <FormControl className={classes.textField}>
          <Autocomplete
            id="clientFilter"
            options={clients}
            filterSelectedOptions
            getOptionLabel={(option: Client) => option.firstName+' '+option.surname}
            onChange={(event: SyntheticEvent<Element, Event>, value: Client | null)=>{
              setSelectedClient(value);
            }}
            renderInput={params => (
              <TextField
                {...params}
                variant="standard"
                placeholder={t('CLIENT')}
              />
            )}
            renderOption={(props, item) => (
              <li {...props} key={item.clientId}>
                <ListItemText>{item.firstName+' '+item.surname}</ListItemText>
              </li>
            )}
          />
        </FormControl>

        <span className={classes.separator} />

        {isApexUser(user) && !isDistributorUser(user) && !isClientUser() && (
          <Tooltip title={t('DEPOSIT_WITHDRAWAL')}><div>
            <ColoredIconButton
              className={classes.icon}
              onClick={() => {
                dispatch(openDialog(DEPOSIT_DIALOG_ID, { client }))
              }}
              size="small"
              icon={<Add />}
            /></div>
          </Tooltip>
        )}

        {canExportClientDetail(user) && (
          <Tooltip title={t('EXPORT_XLS')}><div>
            <ColoredIconButton
              className={classes.icon}
              onClick={() => setShowExportConfirmation(true)}
              size="small"
              icon={<DownloadIcon />}
            /></div>
          </Tooltip>
        )}

      </Toolbar>

      <AlertMessageConfirmation
        alertOpen={showExportConfirmation}
        onOk={() => {
          exportClientDetail(client.clientId)
          setShowExportConfirmation(false)
        }}
        onCancle={() => setShowExportConfirmation(false)}
        alertText={t('CLIENT_EXPORT_MESSAGE')}
      />
    </AppBar>
  )
}
